/**
 * What `uname` says, and which Environment Service Executable that machine
 * needs.
 *
 * This mapping was written twice — once in the bootstrap script that installs
 * prerequisites on a remote machine, once in the probe that verifies one — and
 * the two had to agree for an install to land on the platform it was tested on.
 * They are one statement here, in the two forms the flow needs: a TypeScript
 * answer for Desktop, which decides *which* asset to push, and a shell fragment
 * for the remote, which has nothing but `uname` to go on.
 *
 * Windows is absent because `uname` is: AFAR Connect Computer Setup reaches a
 * machine over SSH with a POSIX login shell.
 */
import {
  ENVIRONMENT_SERVICE_EXECUTABLE_TARGETS,
  type EnvironmentServiceExecutableTarget,
} from "./environmentServiceExecutablePolicy.ts";

export interface UnameTarget {
  /** `uname -s` */
  readonly os: string;
  /** `uname -m` */
  readonly machine: string;
  readonly platform: string;
  readonly architecture: string;
}

/**
 * The trap this table exists for: one architecture has two spellings, and which
 * one a machine uses depends on the operating system. Apple silicon says
 * `arm64`; the same silicon running Linux says `aarch64`.
 */
const UNAME_SPELLINGS: Readonly<
  Record<string, { readonly os: string; readonly machines: Readonly<Record<string, string>> }>
> = {
  darwin: { os: "Darwin", machines: { arm64: "arm64", x64: "x86_64" } },
  linux: { os: "Linux", machines: { arm64: "aarch64", x64: "x86_64" } },
};

/** Every target a POSIX machine can report itself as, in the policy's own order. */
export const UNAME_TARGETS: readonly UnameTarget[] = ENVIRONMENT_SERVICE_EXECUTABLE_TARGETS.flatMap(
  (target) => {
    const spelling = UNAME_SPELLINGS[target.platform];
    const machine = spelling?.machines[target.architecture];
    if (spelling === undefined || machine === undefined) return [];
    return [
      { os: spelling.os, machine, platform: target.platform, architecture: target.architecture },
    ];
  },
);

export const environmentServiceTargetForUname = (
  os: string,
  machine: string,
): Pick<EnvironmentServiceExecutableTarget, "platform" | "architecture"> | undefined => {
  const found = UNAME_TARGETS.find(
    (candidate) => candidate.os === os && candidate.machine === machine,
  );
  return found === undefined
    ? undefined
    : { platform: found.platform, architecture: found.architecture };
};

/** How a platform is spelled wherever one string has to stand for both halves. */
export const environmentServicePlatformSlug = (
  target: Pick<EnvironmentServiceExecutableTarget, "platform" | "architecture">,
): string => `${target.platform}-${target.architecture}`;

/**
 * The `case` a remote shell runs to answer the same question. Generated from the
 * same table, so a machine Desktop believes it can serve is a machine the remote
 * agrees it is — and a disagreement is a refusal rather than a wrong binary.
 */
export const unamePlatformCaseScript = (variable: string): string =>
  [
    `case "$(uname -s)/$(uname -m)" in`,
    ...UNAME_TARGETS.map(
      (target) =>
        `${target.os}/${target.machine}) ${variable}=${environmentServicePlatformSlug(target)} ;;`,
    ),
    `*) ${variable}=unsupported ;;`,
    "esac",
  ].join("\n");
